import React, { useMemo, useRef } from 'react'
import { Link } from 'react-router-dom'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import styled from 'styled-components'
import type { HeadingOutlineItem } from './headingOutline'

const Prose = styled.div`
  color: ${({ theme }) => theme.colors.gray2};
  font-size: 16px;
  line-height: 1.65;
  max-width: 100%;

  h1 {
    color: ${({ theme }) => theme.colors.gray1};
    font-size: 28px;
    font-weight: 600;
    margin: 0 0 16px;
    line-height: 1.25;
    scroll-margin-top: 96px;
  }

  h2 {
    color: ${({ theme }) => theme.colors.gray1};
    font-size: 20px;
    font-weight: 600;
    margin: 28px 0 12px;
    line-height: 1.3;
    scroll-margin-top: 96px;
  }

  h3 {
    color: ${({ theme }) => theme.colors.gray1};
    font-size: 17px;
    font-weight: 600;
    margin: 20px 0 8px;
    scroll-margin-top: 96px;
  }

  p {
    margin: 0 0 14px;
  }

  ul,
  ol {
    margin: 0 0 14px;
    padding-left: 1.35em;
  }

  li {
    margin-bottom: 6px;
  }

  a {
    color: ${({ theme }) => theme.colors.cyan};
    text-decoration: underline;
    text-underline-offset: 2px;
  }

  code {
    font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New', monospace;
    font-size: 0.9em;
    background: ${({ theme }) => theme.colors.gray7};
    padding: 2px 6px;
    border-radius: 4px;
  }

  pre {
    background: ${({ theme }) => theme.colors.gray7};
    padding: 14px 16px;
    border-radius: 8px;
    overflow-x: auto;
    margin: 0 0 16px;
  }

  pre code {
    background: transparent;
    padding: 0;
  }

  blockquote {
    margin: 0 0 16px;
    padding: 8px 0 8px 14px;
    border-left: 3px solid ${({ theme }) => theme.colors.gray5};
    color: ${({ theme }) => theme.colors.gray3};
  }

  table {
    width: 100%;
    border-collapse: collapse;
    margin: 0 0 16px;
    font-size: 14px;
  }

  th,
  td {
    border: 1px solid ${({ theme }) => theme.colors.gray6};
    padding: 8px 10px;
    text-align: left;
    vertical-align: top;
  }

  th {
    background: ${({ theme }) => theme.colors.gray7};
    color: ${({ theme }) => theme.colors.gray1};
    font-weight: 600;
  }

  hr {
    border: 0;
    border-top: 1px solid ${({ theme }) => theme.colors.gray6};
    margin: 24px 0;
  }
`

export type MarkdownBodyProps = {
  markdown: string
  /**
   * Anchors for the documentation's own table of contents. Optional because
   * the front page renders news through the same component and has no contents
   * to link to — a second markdown renderer for the same job would be the more
   * expensive answer.
   */
  outline?: HeadingOutlineItem[]
}

function nextHeadingId(outline: HeadingOutlineItem[], ptr: React.MutableRefObject<number>, level: 2 | 3): string | undefined {
  while (ptr.current < outline.length && outline[ptr.current].level < level) {
    ptr.current += 1
  }
  if (ptr.current >= outline.length || outline[ptr.current].level !== level) {
    return undefined
  }
  const id = outline[ptr.current].id
  ptr.current += 1
  return id
}

export const MarkdownBody: React.FC<MarkdownBodyProps> = ({ markdown, outline = [] }) => {
  const ptr = useRef(0)

  const components = useMemo(
    () => ({
      h2: ({ children, ...rest }: React.HTMLAttributes<HTMLHeadingElement>) => {
        const id = nextHeadingId(outline, ptr, 2)
        return (
          <h2 id={id} {...rest}>
            {children}
          </h2>
        )
      },
      h3: ({ children, ...rest }: React.HTMLAttributes<HTMLHeadingElement>) => {
        const id = nextHeadingId(outline, ptr, 3)
        return (
          <h3 id={id} {...rest}>
            {children}
          </h3>
        )
      },
      /*
       * An internal link is routed, not followed.
       *
       * The Terminal section is the first documentation that links to other
       * documentation, and a plain anchor to `/documentation/…` reloads the
       * whole application to move between two pages of the same one. Anything
       * with a scheme, a protocol-relative host or a fragment is left exactly
       * as written — those are genuinely elsewhere.
       */
      a: ({ children, href, ...rest }: React.AnchorHTMLAttributes<HTMLAnchorElement>) => {
        const internal = href?.startsWith('/') && !href.startsWith('//')
        if (!internal) {
          return (
            <a href={href} target="_blank" rel="noreferrer noopener" {...rest}>
              {children}
            </a>
          )
        }
        return (
          <Link to={href as string} {...rest}>
            {children}
          </Link>
        )
      },
    }),
    [outline],
  )

  ptr.current = 0

  return (
    <Prose>
      <ReactMarkdown remarkPlugins={[remarkGfm]} components={components}>
        {markdown}
      </ReactMarkdown>
    </Prose>
  )
}
